// packages/core/src/learning/schools/relationship-family-money.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Relationships & family money". Matches
// boglehead-indexing.ts gold standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview/exhibit names: ramit-sethi,
//                     morgan-housel, vicki-robin. (Joe Dominguez appears only
//                     as a co-author in canon — not a figures-tally entry, and
//                     not a FigureId.)
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const RELATIONSHIP_FAMILY_MONEY_EXHIBIT: SchoolExhibitEntry = {
	id: 'relationship-family-money',
	tagline:
		'Relationships and family money believes money is never just personal.',
	worldviewSummary:
		'Relationships and family money is paying attention to the people attached to your financial decisions. This lens protects you from pretending money choices happen in a vacuum. It accepts that partners, parents, children, relatives, culture, caregiving, and shared expectations can shape what money means and what money costs. The blind spot is turning every relationship into a budget problem or using money boundaries as an excuse to avoid love, generosity, or responsibility. This lens is strongest when it helps users build trust, name expectations, and protect their future without shaming family ties.',
	coreBeliefs: [
		'Relationships and family money believes money decisions carry emotional history.',
		'Relationships and family money believes trust matters as much as the spreadsheet.',
		'Relationships and family money believes couples need shared language before shared accounts.',
		'Relationships and family money believes family support should be named clearly, not handled through guilt and silence.',
		'Relationships and family money believes boundaries can protect love when money pressure gets heavy.',
		'Relationships and family money believes caregiving, culture, and obligation are real parts of the money picture.',
	],
	warnsAgainst: [
		'Pretending money is only individual when family and relationships shape the choices.',
		'Mixing love, guilt, secrecy, and money without clear agreements.',
		'Letting one partner carry the mental load while both people live with the outcome.',
		'Giving support in ways that destroy your own stability and create quiet resentment.',
	],
	helps: 'This lens helps people sharing money with a partner, supporting family, preparing for marriage, handling caregiving, or trying to break old money patterns without breaking relationships. It may not fit if someone wants a clean formula, because relationship money needs numbers, honesty, timing, and emotional maturity.',
	figures: [],
	canon: [
		{
			title: 'Money for Couples',
			author: 'Ramit Sethi',
			oneLineIdea:
				'A strong money life with a partner starts with honest conversations, shared values, and clear systems.',
		},
		{
			title: 'I Will Teach You to Be Rich',
			author: 'Ramit Sethi',
			oneLineIdea:
				'Money works better when it supports a life people actually want, including shared life decisions.',
		},
		{
			title: 'The Psychology of Money',
			author: 'Morgan Housel',
			oneLineIdea:
				'People bring different histories, fears, and definitions of enough into every money decision.',
		},
		{
			title: 'Your Money or Your Life',
			author: 'Vicki Robin and Joe Dominguez',
			oneLineIdea:
				'Money choices reveal values, time, and the kind of life people are trying to build.',
		},
		{
			title: 'Family money boundaries',
			oneLineIdea:
				'Support works best when the amount, purpose, timing, and limits are spoken clearly.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-relationship-family-money',
	debateIds: [],
	applyActions: [
		{
			label: 'See how relationship obligations affect your current picture',
			action: {
				type: 'openDashboard',
				label: 'Check your current picture',
			},
		},
		{
			label: 'Test how family support changes your runway',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Compare a shared money decision against your real numbers',
			action: {
				type: 'openSimulator',
				label: 'Simulate the shared choice',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Write the money conversation you have been avoiding',
			action: {
				type: 'noToolAction',
				label: 'Prepare the conversation',
				prompt: 'Write the money conversation you have been avoiding, the truth you need to say, and the boundary or agreement you want to propose.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Money is personal, but it is rarely only yours.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost is not always the bill. Sometimes it is resentment.',
			kind: 'hook',
		},
		{
			line: 'What money conversation changed your relationship for the better?',
			kind: 'interview-question',
		},
		{
			line: 'When did helping family start to hurt your own stability?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps people see how family, partners, and real life pressure change the money plan.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Financial education should include the people who actually shape your choices.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Ask a couple what they fight about financially, then ask what they never clearly agreed on.',
			kind: 'creator-prompt',
		},
		{
			line: 'Show how one family support promise can change runway, stress, and future options.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
